'use client';
import {cn} from '@/lib/utils';
import clsx from 'clsx';
import {motion} from 'framer-motion';
import parse from 'html-react-parser';
import {useTranslations} from 'next-intl';
import {useSearchParams} from 'next/navigation';
import {useEffect, useMemo, useRef, useState} from 'react';
import {
  Carousel,
  CarouselApi,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '../ui/carousel';

export default function Albums({
  albums,
  awardeeData
}: {
  albums: any[];
  awardeeData: any[];
}) {
  const searchParams = useSearchParams();
  const t = useTranslations('gallery');

  // 1. Organize data structure
  const {albumsMap, albumKeysList} = useMemo(() => {
    const map: Record<string, {name: string; normal?: any; ai?: any}> = {};
    const keys: string[] = [];

    albums.forEach((a) => {
      const isAi = a.key.includes('-ai');
      const baseKey = a.key.replace('-ai', '');
      if (!map[baseKey]) {
        map[baseKey] = {name: a.name.replace(' (AI)', '')};
        keys.push(baseKey);
      }

      if (isAi) map[baseKey].ai = a;
      else map[baseKey].normal = a;
    });
    return {albumsMap: map, albumKeysList: keys};
  }, [albums]);

  const awardeeMap = awardeeData.reduce(
    (acc, a) => {
      acc[a.code] = a;
      return acc;
    },
    {} as Record<string, any>
  );

  // 2. Initialize state
  const [activeKey, setActiveKey] = useState<string>(() => {
    const param = searchParams.get('album');
    if (param && albums.find((a) => a.key === param)) return param;
    return albums[0]?.key || '';
  });

  // 3. Compute current state
  const currentBaseKey = activeKey.replace('-ai', '');
  const isAiMode = activeKey.includes('-ai');

  // [Fix] Ensure currentAlbumData always has a value: prefer current mode, otherwise fallback to the other mode
  const group = albumsMap[currentBaseKey];
  const currentAlbumData = isAiMode
    ? group?.ai || group?.normal
    : group?.normal || group?.ai;

  // [Fix] If only a single version exists, force sync mode
  const effectiveIsAi = !!(isAiMode && group?.ai) || !group?.normal;

  const images = (currentAlbumData?.photos || []).sort((a: any, b: any) => {
    return activeKey.startsWith('you-are-star') ||
      currentAlbumData?.sort_by_name
      ? a.name.localeCompare(b.name)
      : 0;
  });

  // UI state
  const [isOpenCarousel, setIsOpenCarousel] = useState<boolean>(false);
  const [carouselApi, setCarouselApi] = useState<CarouselApi | null>(null);
  const [isDisplayMenu, setIsDisplayMenu] = useState(false);
  const [loadedIndices, setLoadedIndices] = useState<Set<number>>(new Set());
  const CarouselPrevRef = useRef<HTMLButtonElement>(null);

  const toggleAiMode = () => {
    const targetKey = effectiveIsAi ? group?.normal?.key : group?.ai?.key;
    if (targetKey) setActiveKey(targetKey);
  };

  const onOffCarousel = (index: number) => {
    if (!isOpenCarousel) {
      setTimeout(() => carouselApi?.scrollTo(index, true), 0);
    }
    setIsOpenCarousel(!isOpenCarousel);
  };

  const addLoadedIndices = (current: number) => {
    const len = images.length;
    if (len === 0) return;
    setLoadedIndices((prev) => {
      const next = new Set(prev);
      for (let offset = -5; offset <= 5; offset++) {
        const targetIndex = (((current + offset) % len) + len) % len;
        next.add(targetIndex);
      }
      return next;
    });
  };

  useEffect(() => {
    if (isOpenCarousel) {
      CarouselPrevRef.current?.focus();
    }

    // apture ESC key
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onOffCarousel(0);
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpenCarousel]);

  useEffect(() => {
    if (!carouselApi || !isOpenCarousel) return;

    const updateLoadedSlides = () => {
      addLoadedIndices(carouselApi.selectedScrollSnap());
    };

    updateLoadedSlides();
    carouselApi.on('select', updateLoadedSlides);
    return () => {
      carouselApi.off('select', updateLoadedSlides);
    };
  }, [carouselApi, isOpenCarousel, images.length]);

  return (
    <div className="text-[18px]">
      {albumKeysList.length > 1 && (
        <div className="lg:hidden flex flex-col xl:flex-row items-center justify-between py-[20px]">
          <div className="w-full text-sm xl::text-lg album-select-list-btn">
            <div className="flex gap-2 bg-black rounded-full flex items-center justify-center text-white p-[0.6vw] m-auto w-fit font-[300] relative ">
              <button
                className="my-2 rounded-full flex items-center min-w-[327px] w-[60vw] justify-center cursor-pointer h-[55px] px-[15px]"
                onClick={() => setIsDisplayMenu(!isDisplayMenu)}
              >
                <div className="text-center selected-album-title">
                  {parse(currentAlbumData?.name || group?.name || '')}
                </div>
              </button>
              <div className="absolute right-[20px] h-full flex items-center justify-center">
                <div className="transition-all duration-300 ease-in-out album-select-list-arrow">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    height="24px"
                    viewBox="0 -960 960 960"
                    width="24px"
                    fill="#ffffff"
                  >
                    <path d="M480-344 240-584l56-56 184 184 184-184 56 56-240 240Z"></path>
                  </svg>
                </div>
              </div>
              <div className="top-full absolute left-0 right-0 bottom-0 bg-black z-[10]">
                <div
                  className={clsx(
                    'album-select-list-box bg-white w-full rounded-md text-black py-3 mt-3 transition-all duration-300 ease-in-out origin-top',
                    isDisplayMenu ? 'scale-y-100' : 'scale-y-0'
                  )}
                >
                  <div className="album-select-list flex flex-col gap-2 items-center justify-center">
                    {albumKeysList.map((baseKey) => (
                      <button
                        key={'menu-m-' + baseKey}
                        className={clsx(
                          'album-select-item cursor-pointer hover:bg-gray-200 flex w-full flex-col items-center justify-center py-2 px-4 text-sm',
                          currentBaseKey === baseKey &&
                            'border-l-[5px] border-l-red-500'
                        )}
                        onClick={() => {
                          setIsDisplayMenu(false);
                          const targetKey =
                            effectiveIsAi && albumsMap[baseKey].ai
                              ? `${baseKey}-ai`
                              : baseKey;
                          setActiveKey(targetKey);
                        }}
                      >
                        <div>{parse(albumsMap[baseKey].name)}</div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col xl:flex-row items-center justify-between py-[20px]">
        <div className="w-full xl:w-1/6"></div>
        <div className="hidden lg:flex w-full xl:w-4/6 text-sm xl::text-lg mb-5">
          <div className="album-selector flex gap-2 bg-black rounded-full flex items-center justify-center text-white px-[10px] m-auto w-fit font-[300]">
            {albumKeysList.length > 1 &&
              albumKeysList.map((baseKey) => (
                <button
                  key={'menu-d-' + baseKey}
                  className={clsx(
                    'my-2 rounded-full border-gray-100 album-select flex items-center justify-center cursor-pointer h-[55px] px-[24px]',
                    currentBaseKey === baseKey && 'font-[500] bg-[#F04B29]'
                  )}
                  onClick={() => {
                    const targetKey =
                      effectiveIsAi && albumsMap[baseKey].ai
                        ? `${baseKey}-ai`
                        : baseKey;
                    setActiveKey(targetKey);
                  }}
                >
                  <div className="text-center">
                    {parse(albumsMap[baseKey].name)}
                  </div>
                </button>
              ))}
          </div>
        </div>

        <div className="w-full xl:w-1/6 flex items-center justify-center">
          {group?.ai && group?.normal && (
            <button
              className="inline-flex items-center justify-center cursor-pointer select-none"
              onClick={toggleAiMode}
            >
              <div className="mr-[0.5vw] text-black text-sm">
                {t('aiVersion')}
              </div>
              <div
                className={clsx(
                  'relative w-11 h-6 rounded-full border-white',
                  effectiveIsAi ? 'bg-[#F04B29]' : 'bg-gray-200'
                )}
              >
                <div
                  className={clsx(
                    'absolute top-[2px] start-[2px] bg-white border-gray-300 border rounded-full h-5 w-5 transition-all',
                    effectiveIsAi ? 'translate-x-full' : 'translate-x-0'
                  )}
                  style={{transform: 'none'}}
                ></div>
              </div>
            </button>
          )}
        </div>
      </div>

      {/* Carousel */}
      <div
        className={cn(
          'fixed h-screen w-full bg-black/50 z-[100] top-0 left-0 transition-opacity duration-500 ease-in-out',
          isOpenCarousel ? 'opacity-100' : 'scale-0 opacity-0'
        )}
        onClick={() => onOffCarousel(0)}
      >
        <div className="absolute right-5 top-5 p-2 z-[150]">
          <button className="rounded-full bg-[#000000] w-8 h-8 flex justify-center items-center text-white cursor-pointer transition-all duration-200">
            <span className="material-symbols outlined">close</span>
          </button>
        </div>
        <Carousel
          setApi={setCarouselApi}
          opts={{loop: true}}
          className="w-full relative group"
        >
          <CarouselContent>
            {images.map((image: any, index: number) => {
              return (
                <CarouselItem key={index}>
                  {loadedIndices.has(index) && (
                    <div className="flex flex-col items-center justify-center text-white">
                      <div className="h-screen w-full overflow-hidden flex justify-center items-center">
                        <img
                          onClick={(e) => {
                            e.stopPropagation();
                          }}
                          className="max-w-[80vw] max-h-[80vh] object-contain"
                          src={image.url}
                          alt={image.name}
                        />
                      </div>
                    </div>
                  )}
                </CarouselItem>
              );
            })}
          </CarouselContent>
          {images.length > 1 && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute top-1/2 -translate-y-1/2 right-30 z-[150]"
            >
              <CarouselNext
                className={clsx(
                  'bg-[#000000] text-white border-none cursor-pointer'
                )}
              />
            </div>
          )}

          {images.length > 1 && (
            <div
              onClick={(e) => e.stopPropagation()}
              className="absolute left-30 top-1/2 -translate-y-1/2 z-[150]"
            >
              <CarouselPrevious
                className={clsx(
                  'bg-[#000000] text-white border-none cursor-pointer'
                )}
                ref={CarouselPrevRef}
              />
            </div>
          )}
        </Carousel>
      </div>

      {/* image list */}
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3 mb-10 px-[20px] md:m-auto max-w-[1440px] ">
        {images.map((image: any, index: number) => {
          let caption = ['', '', ''];
          const imgCode = image.name.substr(0, 5);
          if (awardeeMap[imgCode]) {
            caption = [
              imgCode,
              awardeeMap[imgCode].name,
              awardeeMap[imgCode].school
            ];
          }

          try {
            caption = image.caption.split('|');
          } catch {}
          const [code, name, school] = caption;
          return (
            <motion.button
              key={activeKey + '-' + index}
              initial={{opacity: 0.1, y: 10}}
              animate={{opacity: 1, y: 0}}
              transition={{
                duration: 0.3,
                delay: (index % 100) * 0.03
              }}
              className="w-full flex flex-col items-start cursor-pointer transition-all duration-300 hover:scale-105 text-left"
              onClick={() => {
                onOffCarousel(index);
              }}
            >
              <img
                src={image.formats?.small?.url ?? image.url}
                onMouseEnter={() => {
                  addLoadedIndices(index);
                }}

                alt={
                  name != ''
                    ? name + ' ' + school
                    : currentAlbumData.name.replace(/<\/?[^>]+(>|$)/g, '')
                }
                className="object-cover w-full aspect-square "
              />
              {name != '' && (
                <div className="flex flex-col gap-2 mb-10">
                  <div className="py-1 flex items-center font-[300] text-[13px] text-[#9B9B9B]">
                    {code}
                  </div>
                  <div className="flex items-center font-[700] text-[19px] leading-none">
                    {name}
                  </div>
                  <div className="font-[300] text-[13px] leading-none line-clamp-3 ">
                    {school}
                  </div>
                </div>
              )}
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
